import React, { useEffect, useMemo, useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Calculator,
  Calendar,
  Loader2,
  AlertTriangle,
  TrendingUp,
  Clock,
  ArrowRight,
  Sparkles,
  Building2,
  Mail,
  Phone,
  User,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import {
  ASSUMPTIONS,
  brl,
  calcRoi,
  num,
  qualifyMql,
  type RoiInputs,
} from '@/lib/roiCalculator';
import {
  ERP_OPTIONS,
  getOrCreateDgennyLeadId,
  numberToBuyerTeamBucket,
} from '@/lib/leadOptions';

const SESSION_KEY = 'dgenny-roi-diagnostico';
const WHATSAPP_NUMBER = '5548988509968';

interface StoredDiagnostico {
  nome: string;
  email: string;
  whatsapp: string;
  construtora: string;
  inputs: RoiInputs;
  createdAt: string;
}

interface FormState {
  nome: string;
  email: string;
  whatsapp: string;
  construtora: string;
  erp: string;
  erpOutro: string;
  numObras: string;
  m2Total: string;
  tamanhoEquipeCompras: string;
  donoNoTimeDeCompras: boolean;
}

const emptyForm: FormState = {
  nome: '',
  email: '',
  whatsapp: '',
  construtora: '',
  erp: '',
  erpOutro: '',
  numObras: '',
  m2Total: '',
  tamanhoEquipeCompras: '',
  donoNoTimeDeCompras: false,
};

function formatPhone(raw: string): string {
  const n = raw.replace(/\D/g, '');
  if (n.length <= 2) return n;
  if (n.length <= 7) return `(${n.slice(0, 2)}) ${n.slice(2)}`;
  if (n.length <= 11) return `(${n.slice(0, 2)}) ${n.slice(2, 7)}-${n.slice(7)}`;
  return `(${n.slice(0, 2)}) ${n.slice(2, 7)}-${n.slice(7, 11)}`;
}

const CalculadoraROI: React.FC = () => {
  const { toast } = useToast();
  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [diagnostico, setDiagnostico] = useState<StoredDiagnostico | null>(null);

  // Tenta recuperar diagnóstico da session (se usuário recarregar)
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(SESSION_KEY);
      if (raw) setDiagnostico(JSON.parse(raw));
    } catch {
      // ignora
    }
  }, []);

  const validate = (): boolean => {
    const e: Partial<Record<keyof FormState, string>> = {};
    if (!form.nome.trim()) e.nome = 'Obrigatório';
    if (!form.email.trim()) e.email = 'Obrigatório';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'E-mail inválido';
    if (!form.whatsapp.trim()) e.whatsapp = 'Obrigatório';
    else if (form.whatsapp.replace(/\D/g, '').length < 10) e.whatsapp = 'Telefone inválido';
    if (!form.construtora.trim()) e.construtora = 'Obrigatório';
    if (!form.erp) e.erp = 'Selecione uma opção';
    else if (form.erp === 'Outro' && !form.erpOutro.trim()) e.erpOutro = 'Informe o nome do ERP';
    const nObras = parseFloat(form.numObras);
    const m2 = parseFloat(form.m2Total);
    const team = parseFloat(form.tamanhoEquipeCompras);
    if (!(nObras > 0)) e.numObras = 'Informe um valor > 0';
    if (!(m2 > 0)) e.m2Total = 'Informe um valor > 0';
    if (!(team > 0)) e.tamanhoEquipeCompras = 'Informe um valor > 0';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleChange = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors(prev => ({ ...prev, [key]: undefined }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);

    const urlParams = new URLSearchParams(window.location.search);
    const erpFinal = form.erp === 'Outro' ? form.erpOutro.trim() : form.erp;
    const inputs: RoiInputs = {
      numObras: parseFloat(form.numObras),
      m2Total: parseFloat(form.m2Total),
      tamanhoEquipeCompras: parseFloat(form.tamanhoEquipeCompras),
      donoNoTimeDeCompras: form.donoNoTimeDeCompras,
      erp: erpFinal,
    };

    const stored: StoredDiagnostico = {
      nome: form.nome,
      email: form.email,
      whatsapp: form.whatsapp,
      construtora: form.construtora,
      inputs,
      createdAt: new Date().toISOString(),
    };

    // Calcula pra já compor a mensagem do CRM
    const r = calcRoi(inputs);

    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(stored));

      const dgennyLeadId = getOrCreateDgennyLeadId();
      const tamanhoTimeCompras = numberToBuyerTeamBucket(inputs.tamanhoEquipeCompras);

      // Cria lead no CRM (não bloqueante)
      void fetch('/api/create-lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.nome,
          email: form.email,
          phone: form.whatsapp,
          construtora: form.construtora,
          erp: form.erp,
          erpOutro: form.erpOutro,
          tamanhoTimeCompras,
          numObras: inputs.numObras,
          dgennyLeadId,
          source: 'calculadora_roi',
          utm_source: urlParams.get('utm_source') || '',
          utm_medium: urlParams.get('utm_medium') || '',
          utm_campaign: urlParams.get('utm_campaign') || '',
          utm_content: urlParams.get('utm_content') || '',
          utm_term: urlParams.get('utm_term') || '',
          gclid: urlParams.get('gclid') || '',
          fbclid: urlParams.get('fbclid') || '',
          pageUrl: window.location.href,
          whatsAppMessage: [
            `Calculadora de ROI — ${form.construtora}`,
            `ERP: ${erpFinal}`,
            `Obras: ${inputs.numObras} • m²: ${num(inputs.m2Total, 0)}`,
            `Equipe de compras: ${inputs.tamanhoEquipeCompras} (${tamanhoTimeCompras}, dono no time: ${inputs.donoNoTimeDeCompras ? 'sim' : 'não'})`,
            `Volume estimado: ${brl(r.volumeComprasMensal)}/mês`,
            `Perda mensal: ${brl(r.perdaMensal)} • Ganho projetado: ${brl(r.ganhoMensal)}/mês`,
          ].join('\n'),
        }),
      }).catch(() => { /* best effort */ });

      // GTM
      type DataLayerWindow = typeof window & { dataLayer?: Record<string, unknown>[] };
      const dlw = window as DataLayerWindow;
      dlw.dataLayer = dlw.dataLayer || [];
      dlw.dataLayer.push({ event: 'roi_submitted', construtora: form.construtora, volume: r.volumeComprasMensal });

      setDiagnostico(stored);
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50);
    } catch (err) {
      console.error(err);
      toast({ title: 'Erro ao processar', description: 'Tente novamente em instantes.', variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navbar />
      <main className="flex-1">
        {diagnostico ? (
          <Diagnostico data={diagnostico} onReset={() => {
            sessionStorage.removeItem(SESSION_KEY);
            setDiagnostico(null);
            setForm(emptyForm);
          }} />
        ) : (
          <>
            {/* Hero */}
            <section className="bg-gradient-to-br from-brand-azul via-brand-azul to-brand-roxo/80 text-white py-20 px-4">
              <div className="max-w-3xl mx-auto text-center">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-sm font-medium mb-6">
                  <Calculator className="h-4 w-4" />
                  Diagnóstico de ROI em compras
                </div>
                <h1 className="!text-white font-display text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 leading-tight">
                  Descubra quanto sua construtora <span className="!text-brand-rosa">deixa na mesa</span> todo mês
                </h1>
                <p className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto">
                  Em menos de 1 minuto, um diagnóstico personalizado com suas perdas em compras e o retorno que a dgenny entregaria.
                </p>
              </div>
            </section>

            {/* Formulário */}
            <section className="py-16 px-4 -mt-12">
              <div className="max-w-2xl mx-auto">
                <Card className="p-8 shadow-xl border-brand-azul/10">
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid md:grid-cols-2 gap-5">
                      <FieldText label="Seu nome" icon={<User className="h-4 w-4" />} value={form.nome} onChange={v => handleChange('nome', v)} error={errors.nome} placeholder="Como podemos te chamar?" />
                      <FieldText label="Construtora" icon={<Building2 className="h-4 w-4" />} value={form.construtora} onChange={v => handleChange('construtora', v)} error={errors.construtora} placeholder="Nome da sua empresa" />
                      <FieldText label="Email corporativo" icon={<Mail className="h-4 w-4" />} type="email" value={form.email} onChange={v => handleChange('email', v)} error={errors.email} placeholder="voce@suaempresa.com.br" />
                      <FieldText label="WhatsApp" icon={<Phone className="h-4 w-4" />} value={form.whatsapp} onChange={v => handleChange('whatsapp', formatPhone(v))} error={errors.whatsapp} placeholder="(00) 00000-0000" />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-brand-azul/80">ERP que vocês usam</Label>
                      <Select value={form.erp} onValueChange={v => handleChange('erp', v)}>
                        <SelectTrigger className={errors.erp ? 'border-red-500' : ''}>
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent>
                          {ERP_OPTIONS.map(opt => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.erp && <p className="text-xs text-red-500">{errors.erp}</p>}
                      {form.erp === 'Outro' && (
                        <div className="pt-2">
                          <Input
                            placeholder='Qual? (ex: "SAP", "sistema próprio", "nenhum")'
                            value={form.erpOutro}
                            onChange={e => handleChange('erpOutro', e.target.value)}
                            className={errors.erpOutro ? 'border-red-500' : ''}
                          />
                          {errors.erpOutro && <p className="text-xs text-red-500 mt-1">{errors.erpOutro}</p>}
                        </div>
                      )}
                    </div>

                    <div className="grid md:grid-cols-3 gap-5">
                      <FieldText label="Obras em andamento" value={form.numObras} onChange={v => handleChange('numObras', v.replace(/[^\d]/g, ''))} error={errors.numObras} placeholder="5" inputMode="numeric" />
                      <FieldText label="m² construindo (total)" value={form.m2Total} onChange={v => handleChange('m2Total', v.replace(/[^\d]/g, ''))} error={errors.m2Total} placeholder="15000" inputMode="numeric" hint="Soma dos m² de todas as obras ativas." />
                      <FieldText label="Equipe de compras" value={form.tamanhoEquipeCompras} onChange={v => handleChange('tamanhoEquipeCompras', v.replace(/[^\d]/g, ''))} error={errors.tamanhoEquipeCompras} placeholder="3" inputMode="numeric" hint="Quantas pessoas compram hoje." />
                    </div>

                    <div className="flex items-start gap-3 pt-2">
                      <Checkbox
                        id="donoNoTime"
                        checked={form.donoNoTimeDeCompras}
                        onCheckedChange={v => handleChange('donoNoTimeDeCompras', v === true)}
                        className="mt-1"
                      />
                      <Label htmlFor="donoNoTime" className="text-sm text-brand-azul/80 cursor-pointer leading-relaxed">
                        O dono / sócio está no time de compras (ele ou ela cota pessoalmente)
                      </Label>
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      disabled={isSubmitting}
                      className="w-full bg-brand-roxo hover:bg-brand-roxo/90 text-white font-semibold rounded-full py-6 mt-4"
                    >
                      {isSubmitting ? (
                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Calculando...</>
                      ) : (
                        <>Gerar meu diagnóstico <ArrowRight className="ml-2 h-4 w-4" /></>
                      )}
                    </Button>

                    <p className="text-xs text-center text-brand-azul/60">
                      Ao continuar, você concorda com nossa{' '}
                      <a href="/politica-de-privacidade" className="underline" target="_blank">Política de Privacidade</a>.
                    </p>
                  </form>
                </Card>
              </div>
            </section>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
};

// ─── Diagnóstico (página de resultado) ──────────────────────────────────

interface DiagnosticoProps {
  data: StoredDiagnostico;
  onReset: () => void;
}

const Diagnostico: React.FC<DiagnosticoProps> = ({ data, onReset }) => {
  const r = useMemo(() => calcRoi(data.inputs), [data.inputs]);
  const mql = useMemo(() => qualifyMql(data.inputs, r), [data.inputs, r]);
  const primeiroNome = data.nome.trim().split(/\s+/)[0] ?? 'amigo';

  const mqlMessage = [
    `Olá, sou ${data.nome} da ${data.construtora}.`,
    `Gerei meu diagnóstico de ROI no site da dgenny e quero agendar uma reunião.`,
    ``,
    `Resumo:`,
    `• Volume estimado: ${brl(r.volumeComprasMensal)}/mês`,
    `• Perda mensal hoje: ${brl(r.perdaMensal)}`,
    `• Ganho projetado: ${brl(r.ganhoMensal)}/mês (${num(r.roi)}x em ${num(r.payback)} meses)`,
  ].join('\n');

  return (
    <>
      {/* Hero */}
      <section className="bg-gradient-to-br from-brand-azul via-brand-azul to-brand-roxo/80 text-white py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <button onClick={onReset} className="text-xs text-white/60 hover:text-white mb-6 underline">← refazer diagnóstico</button>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-xs font-medium mb-4">
            <Sparkles className="h-3 w-3" />
            Diagnóstico personalizado
          </div>
          <h1 className="!text-white font-display text-3xl md:text-5xl font-extrabold mb-3 leading-tight">
            {data.construtora}, seu diagnóstico
          </h1>
          <p className="text-lg text-white/80 max-w-2xl">
            Olá {primeiroNome}. Analisamos os números que você compartilhou e identificamos o impacto de não capturar o potencial completo das suas compras.
          </p>
        </div>
      </section>

      {/* Situação atual */}
      <section className="py-10 px-4 bg-white border-b border-brand-azul/10">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs uppercase tracking-wide font-semibold text-brand-azul/50 mb-4">Situação atual que você nos contou</p>
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <DataRow label="Obras em andamento" value={`${data.inputs.numObras}`} />
            <DataRow label="m² construindo" value={num(data.inputs.m2Total, 0)} />
            <DataRow label="Equipe de compras" value={`${data.inputs.tamanhoEquipeCompras} ${data.inputs.tamanhoEquipeCompras === 1 ? 'pessoa' : 'pessoas'}`} />
            <DataRow label="ERP" value={data.inputs.erp} />
          </div>
        </div>
      </section>

      {/* Perdas identificadas */}
      <section className="py-16 px-4 bg-brand-azul text-white">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-2 mb-3 text-brand-rosa">
            <AlertTriangle className="h-5 w-5" />
            <span className="text-sm font-semibold uppercase tracking-wide">O problema</span>
          </div>
          <h2 className="!text-white font-display text-3xl md:text-4xl font-extrabold mb-3">
            Você deixa <span className="!text-brand-rosa">{brl(r.perdaMensal)}</span> na mesa todo mês
          </h2>
          <p className="text-white/80 max-w-2xl mb-10">
            Em um ano, isso soma <strong className="text-brand-rosa">{brl(r.perdaAnual)}</strong>. Três fontes de perda, todas mensuráveis:
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            <PerdaCard
              icon={<AlertTriangle className="h-5 w-5" />}
              title="Saving não capturado"
              value={r.savingNaoCapturado}
              desc="Preço que você pagaria a menos se cada cotação fosse trabalhada com mais fornecedores e mais rodadas."
            />
            <PerdaCard
              icon={<Clock className="h-5 w-5" />}
              title="Tempo operacional perdido"
              value={r.tempoOperacionalPerdido}
              desc="Horas do seu time queimadas em cotação manual — WhatsApp, planilha, ligações, comparações."
            />
            <PerdaCard
              icon={<Calendar className="h-5 w-5" />}
              title="Atrasos por compra"
              value={r.custoDeAtrasos}
              desc="Obras que param por falta de material ou fornecedor que não entrega. Canteiro parado custa caro."
            />
          </div>
        </div>
      </section>

      {/* Memória de cálculo */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-display text-2xl md:text-3xl font-extrabold text-brand-azul mb-2">Como chegamos nesses números</h2>
          <p className="text-brand-azul/70 mb-8 max-w-2xl">
            Partimos dos dados que você informou e cruzamos com médias do setor da construção civil brasileira. Você pode calibrar depois, direto com nosso time.
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            <Card className="p-6 border-brand-azul/10">
              <p className="text-xs uppercase tracking-wide font-semibold text-brand-azul/50 mb-3">Volume de compras estimado</p>
              <p className="text-2xl font-display font-extrabold text-brand-azul mb-2">{brl(r.volumeComprasMensal)}<span className="text-sm font-normal text-brand-azul/60">/mês</span></p>
              <p className="text-sm text-brand-azul/70 leading-relaxed">
                {num(data.inputs.m2Total, 0)} m² × {brl(ASSUMPTIONS.custoComprasPorM2)} de compras por m² (média setor) ÷ {ASSUMPTIONS.duracaoObraMeses} meses de obra média.
              </p>
            </Card>

            <Card className="p-6 border-brand-azul/10">
              <p className="text-xs uppercase tracking-wide font-semibold text-brand-azul/50 mb-3">Saving não capturado</p>
              <p className="text-2xl font-display font-extrabold text-brand-azul mb-2">{brl(r.savingNaoCapturado)}<span className="text-sm font-normal text-brand-azul/60">/mês</span></p>
              <p className="text-sm text-brand-azul/70 leading-relaxed">
                {brl(r.volumeComprasMensal)} × 5% de saving médio perdido por cotação ter poucos fornecedores ou poucas rodadas.
              </p>
            </Card>

            <Card className="p-6 border-brand-azul/10">
              <p className="text-xs uppercase tracking-wide font-semibold text-brand-azul/50 mb-3">Tempo operacional perdido</p>
              <p className="text-2xl font-display font-extrabold text-brand-azul mb-2">{brl(r.tempoOperacionalPerdido)}<span className="text-sm font-normal text-brand-azul/60">/mês</span></p>
              <p className="text-sm text-brand-azul/70 leading-relaxed">
                {data.inputs.tamanhoEquipeCompras} {data.inputs.tamanhoEquipeCompras === 1 ? 'pessoa' : 'pessoas'} × {ASSUMPTIONS.horasDiaCotando}h/dia × {ASSUMPTIONS.diasUteisMes} dias úteis × {brl(ASSUMPTIONS.custoHoraComprador)}/hora (custo-hora médio com encargos).
              </p>
            </Card>

            <Card className="p-6 border-brand-azul/10">
              <p className="text-xs uppercase tracking-wide font-semibold text-brand-azul/50 mb-3">Custo de atrasos</p>
              <p className="text-2xl font-display font-extrabold text-brand-azul mb-2">{brl(r.custoDeAtrasos)}<span className="text-sm font-normal text-brand-azul/60">/mês</span></p>
              <p className="text-sm text-brand-azul/70 leading-relaxed">
                {data.inputs.numObras} {data.inputs.numObras === 1 ? 'obra' : 'obras'} × {ASSUMPTIONS.atrasosMesPorObra} atrasos/mês por obra × {brl(r.custoSemanaAtraso)} por semana parada. Custo indexado no tamanho médio das suas obras ({num(r.m2MedioPorObra, 0)} m² × R$ {ASSUMPTIONS.custoSemanaAtrasoPorM2}/m² — mão-de-obra ociosa + equipamento alugado + overhead).
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Oportunidade com dgenny */}
      <section className="py-16 px-4 bg-gradient-to-b from-brand-rosa/10 to-white">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-2 mb-3 text-brand-roxo">
            <TrendingUp className="h-5 w-5" />
            <span className="text-sm font-semibold uppercase tracking-wide">A oportunidade</span>
          </div>
          <h2 className="font-display text-3xl md:text-4xl font-extrabold text-brand-azul mb-3">
            Com a dgenny, você recupera <span className="text-brand-roxo">{brl(r.ganhoAnual)}</span> por ano
          </h2>
          <p className="text-brand-azul/70 max-w-2xl mb-10">
            A dgenny é uma IA que conduz compras B2B. Ela cota por você, negocia com múltiplos fornecedores, integra com seu ERP e devolve o tempo do seu time pro que importa.
          </p>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <GanhoCard title="Ganho mensal" main={brl(r.ganhoMensal)} sub="recuperados todo mês" />
            <GanhoCard title="ROI projetado" main={`${num(r.roi)}x`} sub="sobre o investimento" highlight />
            <GanhoCard
              title="Payback"
              main={r.payback < 1 ? 'No 1º mês' : r.payback < 2 ? '1 mês' : `${num(r.payback, 0)} meses`}
              sub="pra pagar a dgenny"
            />
          </div>

          <Card className="p-6 border-brand-azul/10 bg-white">
            <p className="text-sm font-semibold text-brand-azul mb-3">De onde vem a recuperação</p>
            <div className="grid md:grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-brand-azul/60 mb-1">Saving capturado</p>
                <p className="font-display text-xl font-bold text-brand-roxo">{brl(r.savingCapturado)}</p>
                <p className="text-xs text-brand-azul/50">5% do volume, com todas cotações bem trabalhadas</p>
              </div>
              <div>
                <p className="text-brand-azul/60 mb-1">Tempo devolvido</p>
                <p className="font-display text-xl font-bold text-brand-roxo">{brl(r.tempoDevolvido)}</p>
                <p className="text-xs text-brand-azul/50">80% das horas que a equipe perde hoje</p>
              </div>
              <div>
                <p className="text-brand-azul/60 mb-1">Atrasos evitados</p>
                <p className="font-display text-xl font-bold text-brand-roxo">{brl(r.reducaoAtrasos)}</p>
                <p className="text-xs text-brand-azul/50">50% dos atrasos por problema de compra</p>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* CTA condicional */}
      <section className="py-16 px-4 bg-brand-azul text-white">
        <div className="max-w-4xl mx-auto">
          {mql.isMql ? (
            <MqlCta mqlMessage={mqlMessage} />
          ) : (
            <NonMqlCta razoes={mql.razoes} />
          )}
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-8 px-4 bg-white border-t border-brand-azul/10">
        <p className="text-xs text-brand-azul/50 text-center max-w-3xl mx-auto leading-relaxed">
          Projeção baseada em premissas conservadoras do setor da construção civil: 5% de saving sobre o volume de compras, 50% de redução no tempo operacional cotando e 10% de redução em atrasos por problema de compra. Volume de compras estimado em R$ 2.500/m² ao longo de 18 meses de obra. Custo de obra parada indexado em R$ {ASSUMPTIONS.custoSemanaAtrasoPorM2}/m² por semana. Seu resultado real varia conforme mix de obras, qualidade do processo atual e adesão dos fornecedores.
        </p>
      </section>
    </>
  );
};

// ─── Componentes auxiliares ─────────────────────────────────────────────

interface FieldTextProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  placeholder?: string;
  type?: string;
  icon?: React.ReactNode;
  hint?: string;
  inputMode?: 'text' | 'numeric' | 'decimal' | 'email' | 'tel';
}

const FieldText: React.FC<FieldTextProps> = ({ label, value, onChange, error, placeholder, type = 'text', icon, hint, inputMode }) => (
  <div className="space-y-2">
    <Label className="text-sm font-medium text-brand-azul/80 flex items-center gap-2">
      {icon}
      {label}
    </Label>
    <Input
      type={type}
      inputMode={inputMode}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className={error ? 'border-red-500 focus:ring-red-500' : ''}
    />
    {error && <p className="text-xs text-red-500">{error}</p>}
    {!error && hint && <p className="text-xs text-brand-azul/50">{hint}</p>}
  </div>
);

const DataRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <p className="text-xs text-brand-azul/60 uppercase tracking-wide mb-1">{label}</p>
    <p className="font-semibold text-brand-azul">{value}</p>
  </div>
);

const PerdaCard: React.FC<{ icon: React.ReactNode; title: string; value: number; desc: string }> = ({ icon, title, value, desc }) => (
  <Card className="p-6 bg-white/5 border-white/10 backdrop-blur-sm">
    <div className="flex items-center gap-2 mb-3 text-brand-rosa">
      {icon}
      <p className="text-sm font-semibold uppercase tracking-wide">{title}</p>
    </div>
    <p className="font-display text-3xl font-extrabold mb-3 text-white">{brl(value)}<span className="text-sm font-normal text-white/60">/mês</span></p>
    <p className="text-sm text-white/70 leading-relaxed">{desc}</p>
  </Card>
);

const GanhoCard: React.FC<{ title: string; main: string; sub: string; highlight?: boolean }> = ({ title, main, sub, highlight }) => (
  <Card className={`p-6 text-center ${highlight ? 'bg-brand-roxo text-white border-0 shadow-xl scale-[1.03]' : 'bg-white border-brand-azul/10'}`}>
    <p className={`text-xs uppercase tracking-wide font-semibold mb-2 ${highlight ? 'text-brand-rosa' : 'text-brand-azul/60'}`}>{title}</p>
    <p className={`font-display text-3xl md:text-4xl font-extrabold mb-1 ${highlight ? 'text-white' : 'text-brand-roxo'}`}>{main}</p>
    <p className={`text-sm ${highlight ? 'text-white/80' : 'text-brand-azul/60'}`}>{sub}</p>
  </Card>
);

const MqlCta: React.FC<{ mqlMessage: string }> = ({ mqlMessage }) => (
  <div className="text-center">
    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-rosa text-brand-azul text-xs font-bold mb-4">
      <Sparkles className="h-3 w-3" />
      seu perfil se encaixa
    </div>
    <h2 className="!text-white font-display text-3xl md:text-4xl font-extrabold mb-4 leading-tight">
      Bora conversar? Em 30 minutos a gente mostra na prática.
    </h2>
    <p className="text-white/80 max-w-2xl mx-auto mb-8">
      Demonstração personalizada com suas obras e seus fornecedores. Sem compromisso. Se não fizer sentido, você fica com o diagnóstico mesmo assim.
    </p>
    <Button
      size="lg"
      className="bg-brand-rosa hover:bg-brand-rosa/90 text-brand-azul font-bold rounded-full py-6 px-8"
      onClick={() => window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(mqlMessage)}`, '_blank', 'noopener,noreferrer')}
    >
      Agendar demonstração
      <ArrowRight className="ml-2 h-4 w-4" />
    </Button>
  </div>
);

// TODO: caminho não-MQL ainda a definir (redes sociais / vídeo vendas / plano pré-pago anual).
// Placeholder gentil por ora.
const NonMqlCta: React.FC<{ razoes: string[] }> = ({ razoes }) => (
  <div className="text-center">
    <h2 className="!text-white font-display text-2xl md:text-3xl font-extrabold mb-4 leading-tight">
      Valeu pelo diagnóstico!
    </h2>
    <p className="text-white/80 max-w-2xl mx-auto mb-3">
      O seu perfil ainda tá um pouco distante do nosso encaixe ideal {razoes.length > 0 && <span className="text-white/60">({razoes.join(' · ').toLowerCase()})</span>}, mas temos conteúdo que pode te ajudar enquanto vocês crescem.
    </p>
    <p className="text-white/60 max-w-2xl mx-auto mb-8 text-sm">
      Em breve a dgenny vai lançar um plano pré-pago anual que cabe no seu porte. Quer ser avisado primeiro?
    </p>
    <div className="flex flex-col sm:flex-row gap-3 justify-center">
      <a
        href="https://www.linkedin.com/company/dgenny"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-white/10 border border-white/20 text-white font-semibold hover:bg-white/15 transition"
      >
        Seguir dgenny no LinkedIn
      </a>
      <a
        href="https://www.instagram.com/dgenny.com.br"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-white/10 border border-white/20 text-white font-semibold hover:bg-white/15 transition"
      >
        Seguir no Instagram
      </a>
    </div>
  </div>
);

export default CalculadoraROI;
